import React, { useState, useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import axios from "axios";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import { BASE_URL } from "../../Shared/URL.js";
import axiosInstance from "../../Shared/axiosInstance";
import CustomDatePicker from "../CustomDate/CustomDatePicker.jsx";

export default function PurchaseForm() {
  const API_BASE = "/Purchase";
  const SUPPLIERS_API = "/Supplier/all";
  const PRODUCTS_API = "/Product";

  const navigate = useNavigate();

  const [suppliers, setSuppliers] = useState([]);
  const [supplierSearch, setSupplierSearch] = useState("");
  const [loadingSuppliers, setLoadingSuppliers] = useState(false);

  const [products, setProducts] = useState([]);
  const [productSearch, setProductSearch] = useState("");
  const [loadingProducts, setLoadingProducts] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [currentItemIndex, setCurrentItemIndex] = useState(null);

  // Fetch suppliers
  useEffect(() => {
    const fetchSuppliers = async () => {
      try {
        setLoadingSuppliers(true);
        const { data } = await axiosInstance.get(SUPPLIERS_API, {
          params: {
            name: supplierSearch || undefined,
            pageNumber: 1,
            pageSize: 10,
          },
        });
        setSuppliers(data.data || []);
      } catch (err) {
        console.error("Error fetching suppliers:", err);
        Swal.fire("Error", "Failed to load suppliers", "error");
      } finally {
        setLoadingSuppliers(false);
      }
    };

    const delay = setTimeout(fetchSuppliers, 300);
    return () => clearTimeout(delay);
  }, [supplierSearch]);

  // Fetch products (only when modal is open)
  useEffect(() => {
    if (!showModal) return;

    const fetchProducts = async () => {
      try {
        setLoadingProducts(true);
        const { data } = await axiosInstance.get(PRODUCTS_API, {
          params: {
            name: productSearch || undefined,
            pageNumber: 1,
            pageSize: 15,
          },
        });
        setProducts(data.data || []);
      } catch (err) {
        console.error("Error fetching products:", err);
        Swal.fire("Error", "Failed to load products", "error");
      } finally {
        setLoadingProducts(false);
      }
    };

    const delay = setTimeout(fetchProducts, 300);
    return () => clearTimeout(delay);
  }, [productSearch, showModal]);

  // Formik with Yup validation
  const formik = useFormik({
    initialValues: {
      supplierId: "",
      purchaseDate: new Date().toISOString().slice(0, 10),
      items: [{ productId: "", productName: "", quantity: 0, buyPrice: 0 }],
      payments: [
        { amount: 0, date: new Date().toISOString().slice(0, 10), method: "Cash" },
      ],
      discountAmount: 0,
    },
    validationSchema: Yup.object({
      supplierId: Yup.string().required("Supplier is required"),
      items: Yup.array().of(
        Yup.object({
          productId: Yup.string().required("Product is required"),
          quantity: Yup.number()
            .required("Quantity is required")
            .min(1, "Quantity must be at least 1"),
          buyPrice: Yup.number()
            .required("Buy Price is required")
            .min(0, "Buy Price cannot be negative"),
        })
      ),
      payments: Yup.array().of(
        Yup.object({
          amount: Yup.number()
            .required("Amount is required")
            .min(0, "Amount cannot be negative"),
          date: Yup.string().required("Date is required"),
          method: Yup.string().required("Payment method is required"),
        })
      ),
    }),
    onSubmit: async (values) => {
      try {
        const payload = {
          supplierId: values.supplierId,
          purchaseDate: values.purchaseDate,
          items: values.items.map((item) => ({
            productId: item.productId,
            quantity: parseInt(item.quantity) || 0,
            buyPrice: parseFloat(item.buyPrice) || 0,
          })),
          payments: values.payments.map((p) => ({
            amount: parseFloat(p.amount) || 0,
            date: p.date,
            method: p.method,
          })),
          discountAmount: parseFloat(values.discountAmount) || 0,
        };

        const { data } = await axiosInstance.post(API_BASE, payload);
        await Swal.fire({
          title: "Success!",
          text: data.message || "Purchase Created Successfully",
          icon: "success",
          timer: 1500,
          showConfirmButton: false,
        });

        navigate("/purchases");
        formik.resetForm();
      } catch (err) {
        console.error("Error:", err);
        Swal.fire(
          "Error",
          err.response?.data?.message || "Something went wrong",
          "error"
        );
      }
    },
  });

  const total = formik.values.items.reduce((acc, item) => {
    const qty = parseFloat(item.quantity) || 0;
    const price = parseFloat(item.buyPrice) || 0;
    return acc + qty * price;
  }, 0);

  const totalPayments = formik.values.payments.reduce((acc, p) => {
    return acc + (parseFloat(p.amount) || 0);
  }, 0);

  const totalAfterDiscount = total - (parseFloat(formik.values.discountAmount) || 0);
  const remainingAfterDiscount = totalAfterDiscount - totalPayments;

  const addNewItem = () => {
    formik.setFieldValue("items", [
      ...formik.values.items,
      { productId: "", productName: "", quantity: 0, buyPrice: 0 },
    ]);
  };

  const removeItem = (index) => {
    const newItems = formik.values.items.filter((_, i) => i !== index);
    formik.setFieldValue(
      "items",
      newItems.length ? newItems : [{ productId: "", productName: "", quantity: 0, buyPrice: 0 }]
    );
  };

  const openProductModal = (index) => {
    setCurrentItemIndex(index);
    setShowModal(true);
    setProductSearch("");
  };

  const selectProduct = async (product) => {
    if (currentItemIndex === null) return;

    formik.setFieldValue(`items[${currentItemIndex}].productId`, product.id);
    formik.setFieldValue(
      `items[${currentItemIndex}].productName`,
      `${product.name} - ${product.description}`
    );

    try {
      const { data } = await axiosInstance.get(`${PRODUCTS_API}/${product.id}`);
      const buyPrice = data.data?.buyPrice || 0;
      formik.setFieldValue(`items[${currentItemIndex}].buyPrice`, buyPrice);
    } catch (err) {
      console.error("Error fetching product details:", err);
    }

    setShowModal(false);
    setCurrentItemIndex(null);
  };

  const addNewPayment = () => {
    formik.setFieldValue("payments", [
      ...formik.values.payments,
      { amount: 0, date: new Date().toISOString().slice(0, 10), method: "Cash" },
    ]);
  };

  const removePayment = (index) => {
    const newPayments = formik.values.payments.filter((_, i) => i !== index);
    formik.setFieldValue(
      "payments",
      newPayments.length
        ? newPayments
        : [{ amount: 0, date: new Date().toISOString().slice(0, 10), method: "Cash" }]
    );
  };

  return (
    <div style={{ background: "#f8fafc", minHeight: "100vh", paddingBottom: "40px" }}>
      <div className="container py-5" style={{ maxWidth: "1000px" }}>
        
        {/* Header */}
        <div className="d-flex justify-content-between align-items-center mb-4 mt-5 pt-5">
          <div>
            <h2 style={{ color: "#2563eb", fontWeight: "700", fontSize: "28px", marginBottom: "4px" }}>
              <i className="bi bi-cart-plus me-2"></i>إنشاء فاتورة شراء
            </h2>
            <small style={{ color: "#64748b", fontSize: "14px" }}>
              أضف طلب شراء جديد للمخزون
            </small>
          </div>
          <button
            type="button"
            onClick={() => navigate("/purchases")}
            style={{
              background: "#64748b",
              color: "#fff",
              padding: "10px 20px",
              borderRadius: "6px",
              border: "none",
              fontWeight: "600",
              fontSize: "15px",
              cursor: "pointer",
            }}
          >
            <i className="bi bi-arrow-left me-1"></i> رجوع
          </button>
        </div>

        <form onSubmit={formik.handleSubmit}>
          
          {/* Supplier & Date Section */}
          <div style={cardStyle}>
            <h5 style={sectionTitleStyle}>
              <i className="bi bi-building me-2"></i>معلومات المورد
            </h5>
            
            <div className="row g-3">
              <div className="col-md-8">
                <label style={labelStyle}>البحث عن المورد</label>
                <input
                  type="text"
                  placeholder="🔍 اكتب اسم المورد..."
                  value={supplierSearch}
                  onChange={(e) => setSupplierSearch(e.target.value)}
                  style={inputStyle}
                />
                <select
                  name="supplierId"
                  className={formik.touched.supplierId && formik.errors.supplierId ? "is-invalid" : ""}
                  value={formik.values.supplierId}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  style={{ ...inputStyle, marginTop: "8px" }}
                >
                  <option value="">-- اختر المورد --</option>
                  {loadingSuppliers ? (
                    <option disabled>جارٍ التحميل...</option>
                  ) : suppliers.length > 0 ? (
                    suppliers.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name} - {s.details}
                      </option>
                    ))
                  ) : (
                    <option disabled>لم يتم العثور على موردين</option>
                  )}
                </select>
                {formik.touched.supplierId && formik.errors.supplierId && (
                  <div style={errorStyle}>{formik.errors.supplierId}</div>
                )}
              </div>

              <div className="col-md-4">
                <CustomDatePicker
                  label="تاريخ الشراء"
                  value={formik.values.purchaseDate}
                  onChange={(date) => formik.setFieldValue('purchaseDate', date)}
                  placeholder="اختر تاريخ الشراء"
                />
              </div>
            </div>
          </div>

          {/* Products Section */}
          <div style={cardStyle}>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 style={sectionTitleStyle}>
                <i className="bi bi-box-seam me-2"></i>المنتجات ({formik.values.items.length})
              </h5>
              <button
                type="button"
                onClick={addNewItem}
                style={{
                  background: "#2563eb",
                  color: "#fff",
                  padding: "8px 16px",
                  borderRadius: "6px",
                  border: "none",
                  fontWeight: "600",
                  fontSize: "14px",
                  cursor: "pointer",
                }}
              >
                <i className="bi bi-plus-circle me-1"></i> أضف منتج
              </button>
            </div>

            {/* Compact Table Header */}
            <div className="row g-2 mb-2" style={{ 
              padding: "8px 12px", 
              background: "#f1f5f9", 
              borderRadius: "6px",
              fontWeight: "600",
              fontSize: "13px",
              color: "#475569"
            }}>
              <div className="col-1 text-center">#</div>
              <div className="col-4">المنتج</div>
              <div className="col-2 text-center">الكمية</div>
              <div className="col-2 text-center">سعر الشراء</div>
              <div className="col-2 text-center">المجموع</div>
              <div className="col-1"></div>
            </div>

            {formik.values.items.map((item, index) => (
              <div key={index} style={{
                ...compactItemStyle,
                borderBottom: index < formik.values.items.length - 1 ? "1px solid #e2e8f0" : "none"
              }}>
                <div className="row g-2 align-items-center">
                  {/* Row Number */}
                  <div className="col-1 text-center">
                    <span style={{ color: "#2563eb", fontWeight: "600", fontSize: "14px" }}>
                      {index + 1}
                    </span>
                  </div>

                  {/* Product Selection */}
                  <div className="col-4">
                    <div className="d-flex gap-1">
                      <input
                        type="text"
                        value={item.productName || "لم يتم اختيار منتج"}
                        readOnly
                        style={{ 
                          ...compactInputStyle, 
                          flex: 1, 
                          background: "#f8fafc", 
                          cursor: "default",
                          fontSize: "13px"
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => openProductModal(index)}
                        style={{
                          background: "#2563eb",
                          color: "#fff",
                          padding: "6px 12px",
                          borderRadius: "4px",
                          border: "none",
                          fontWeight: "600",
                          fontSize: "12px",
                          cursor: "pointer",
                          whiteSpace: "nowrap",
                        }}
                      >
                        <i className="bi bi-search"></i>
                      </button>
                    </div>
                    {formik.touched.items?.[index]?.productId &&
                      formik.errors.items?.[index]?.productId && (
                        <div style={{...errorStyle, fontSize: "11px"}}>{formik.errors.items[index].productId}</div>
                      )}
                  </div>

                  {/* Quantity */}
                  <div className="col-2">
                    <input
                      name={`items[${index}].quantity`}
                      type="number"
                      min="0"
                      value={item.quantity}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      onWheel={(e) => e.target.blur()}
                      style={{...compactInputStyle, textAlign: "center"}}
                    />
                    {formik.touched.items?.[index]?.quantity &&
                      formik.errors.items?.[index]?.quantity && (
                        <div style={{...errorStyle, fontSize: "11px"}}>{formik.errors.items[index].quantity}</div>
                      )}
                  </div>

                  {/* Buy Price */}
                  <div className="col-2">
                    <input
                      name={`items[${index}].buyPrice`}
                      type="number"
                      min="0"
                      step="0.01"
                      value={item.buyPrice}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      onWheel={(e) => e.target.blur()}
                      style={{...compactInputStyle, textAlign: "center"}}
                    />
                    {formik.touched.items?.[index]?.buyPrice &&
                      formik.errors.items?.[index]?.buyPrice && (
                        <div style={{...errorStyle, fontSize: "11px"}}>{formik.errors.items[index].buyPrice}</div>
                      )}
                  </div>

                  {/* Subtotal */}
                  <div className="col-2 text-center">
                    <span style={{ 
                      color: "#1e40af", 
                      fontWeight: "600", 
                      fontSize: "13px",
                      background: "#dbeafe",
                      padding: "4px 8px",
                      borderRadius: "4px",
                      display: "inline-block"
                    }}>
                      {((item.quantity || 0) * (item.buyPrice || 0)).toFixed(2)}
                    </span>
                  </div>

                  {/* Remove Button */}
                  <div className="col-1 text-center">
                    {formik.values.items.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeItem(index)}
                        style={{
                          background: "#dc2626",
                          color: "#fff",
                          padding: "4px 8px",
                          borderRadius: "4px",
                          border: "none",
                          fontSize: "12px",
                          cursor: "pointer",
                        }}
                      >
                        <i className="bi bi-trash"></i>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Total Summary */}
          <div style={{
            background: "linear-gradient(135deg, #2563eb 0%, #1e40af 100%)",
            padding: "20px",
            borderRadius: "8px",
            marginBottom: "20px",
            boxShadow: "0 4px 12px rgba(37, 99, 235, 0.3)",
          }}>
            <div className="d-flex justify-content-between align-items-center">
              <span style={{ color: "#fff", fontSize: "18px", fontWeight: "600" }}>
                <i className="bi bi-calculator me-2"></i>إجمالي قيمة الشراء
              </span>
              <span style={{ color: "#fff", fontSize: "28px", fontWeight: "700" }}>
                {total.toFixed(2)} جنيه
              </span>
            </div>
          </div>

          {/* Discount Section */}
          <div style={cardStyle}>
            <h5 style={{...sectionTitleStyle, fontSize: "20px", marginBottom: "20px"}}>
              <i className="bi bi-tag me-2"></i>الخصم
            </h5>
            <div className="mb-3" style={{ maxWidth: "400px" }}>
              <label style={{...labelStyle, fontSize: "16px", marginBottom: "10px"}}>قيمة الخصم (جنيه)</label>
              <input
                type="number"
                min="0"
                step="0.01"
                name="discountAmount"
                placeholder="0.00"
                value={formik.values.discountAmount || 0}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                onWheel={(e) => e.target.blur()}
                style={{...inputStyle, padding: "14px 16px", fontSize: "16px"}}
              />
            </div>

            {/* Summary after discount */}
            <div style={{
              background: "linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)",
              padding: "24px",
              borderRadius: "10px",
              marginTop: "20px",
              borderLeft: "5px solid #2563eb",
              boxShadow: "0 2px 8px rgba(37, 99, 235, 0.15)"
            }}>
              <div className="d-flex justify-content-between align-items-center">
                <span style={{ color: "#1e40af", fontWeight: "700", fontSize: "20px" }}>
                  الإجمالي بعد الخصم:
                </span>
                <span style={{ color: "#1e40af", fontWeight: "800", fontSize: "26px" }}>
                  {totalAfterDiscount.toFixed(2)} جنيه
                </span>
              </div>
            </div>
          </div>

          {/* Payments Section */}
          <div style={cardStyle}>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 style={sectionTitleStyle}>
                <i className="bi bi-cash-stack me-2"></i>المدفوعات
              </h5>
              <button
                type="button"
                onClick={addNewPayment}
                style={{
                  background: "#2563eb",
                  color: "#fff",
                  padding: "8px 16px",
                  borderRadius: "6px",
                  border: "none",
                  fontWeight: "600",
                  fontSize: "14px",
                  cursor: "pointer",
                }}
              >
                <i className="bi bi-plus-circle me-1"></i> أضف دفعة
              </button>
            </div>

            {formik.values.payments.map((p, index) => (
              <div key={index} style={itemCardStyle}>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span style={{ color: "#2563eb", fontWeight: "600", fontSize: "15px" }}>
                    الدفعة رقم #{index + 1}
                  </span>
                  {formik.values.payments.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removePayment(index)}
                      style={{
                        background: "#dc2626",
                        color: "#fff",
                        padding: "4px 12px",
                        borderRadius: "4px",
                        border: "none",
                        fontSize: "13px",
                        fontWeight: "600",
                        cursor: "pointer",
                      }}
                    >
                      <i className="bi bi-trash me-1"></i> إزالة
                    </button>
                  )}
                </div>

                <div className="row g-3">
                  <div className="col-md-4">
                    <label style={labelStyle}>المبلغ (جنيه)</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      name={`payments[${index}].amount`}
                      value={p.amount}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      onWheel={(e) => e.target.blur()}
                      style={inputStyle}
                    />
                  </div>
                  <div className="col-md-4">
                    <CustomDatePicker
                      label="تاريخ الدفع"
                      value={p.date}
                      onChange={(date) => formik.setFieldValue(`payments[${index}].date`, date)}
                      placeholder="اختر تاريخ الدفع"
                    />
                  </div>
                  <div className="col-md-4">
                    <label style={labelStyle}>طريقة الدفع</label>
                    <select
                      name={`payments[${index}].method`}
                      value={p.method}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      style={inputStyle}
                    >
                      <option value="Cash">💵 نقدي</option>
                      <option value="VodafoneCash">📱 فودافون كاش</option>
                      <option value="InstaBay">💳 إنستاباي</option>
                    </select>
                  </div>
                </div>
              </div>
            ))}

            <div style={{
              background: "#f0f9ff",
              padding: "16px",
              borderRadius: "6px",
              marginTop: "16px",
              borderLeft: "3px solid #2563eb"
            }}>
              <span style={{ color: "#1e40af", fontWeight: "600", fontSize: "14px" }}>
                الإجمالي المدفوع: {totalPayments.toFixed(2)} جنيه
              </span>
              <br />
              <span style={{ color: "#1e40af", fontWeight: "600", fontSize: "14px" }}>
                المتبقي بعد الخصم: {remainingAfterDiscount.toFixed(2)} جنيه
              </span>
            </div>
          </div>

          {/* Submit Button */}
          <div className="text-end">
            <button type="submit" style={submitBtnStyle}>
              حفظ الفاتورة
            </button>
          </div>
        </form>
      </div>

      {/* Product Modal */}
      {showModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0, 0, 0, 0.6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
          onClick={() => setShowModal(false)}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: "12px",
              width: "90%",
              maxWidth: "700px",
              maxHeight: "80vh",
              overflow: "hidden",
              boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div style={{
              padding: "20px 24px",
              borderBottom: "1px solid #e2e8f0",
              background: "linear-gradient(135deg, #2563eb 0%, #1e40af 100%)",
            }}>
              <div className="d-flex justify-content-between align-items-center">
                <h5 style={{ color: "#fff", fontWeight: "700", fontSize: "20px", margin: 0 }}>
                  <i className="bi bi-search me-2"></i>اختر المنتج
                </h5>
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  style={{
                    background: "rgba(255, 255, 255, 0.2)",
                    color: "#fff",
                    border: "none",
                    borderRadius: "50%",
                    width: "32px",
                    height: "32px",
                    cursor: "pointer",
                    fontSize: "20px",
                  }}
                >
                  ×
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div style={{ padding: "20px 24px" }}>
              <input
                type="text"
                placeholder="🔍 ابحث عن المنتجات بالاسم..."
                value={productSearch}
                onChange={(e) => setProductSearch(e.target.value)}
                autoFocus
                style={{
                  width: "100%",
                  padding: "12px 16px",
                  border: "2px solid #bfdbfe",
                  borderRadius: "8px",
                  fontSize: "15px",
                  marginBottom: "16px",
                  outline: "none",
                }}
              />

              <div style={{ maxHeight: "400px", overflowY: "auto" }}>
                {loadingProducts ? (
                  <div style={{ textAlign: "center", padding: "40px", color: "#64748b" }}>
                    <div className="spinner-border text-primary" role="status"></div>
                    <p style={{ marginTop: "12px" }}>جارٍ تحميل المنتجات...</p>
                  </div>
                ) : products.length > 0 ? (
                  <div>
                    {products.map((product) => (
                      <button
                        key={product.id}
                        type="button"
                        onClick={() => selectProduct(product)}
                        style={{
                          width: "100%",
                          padding: "16px",
                          marginBottom: "8px",
                          border: "1px solid #e2e8f0",
                          borderRadius: "8px",
                          background: "#fff",
                          textAlign: "left",
                          cursor: "pointer",
                          transition: "all 0.2s ease",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = "#f0f9ff";
                          e.currentTarget.style.borderColor = "#2563eb";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = "#fff";
                          e.currentTarget.style.borderColor = "#e2e8f0";
                        }}
                      >
                        <div style={{ fontWeight: "600", color: "#1e293b", marginBottom: "4px" }}>
                          {product.name}
                        </div>
                        <div style={{ fontSize: "14px", color: "#64748b" }}>
                          {product.description}
                        </div>
                      </button>
                    ))}
                  </div>
                ) : (
                  <div style={{ textAlign: "center", padding: "40px", color: "#64748b" }}>
                    <i className="bi bi-inbox" style={{ fontSize: "48px", marginBottom: "12px" }}></i>
                    <p>لم يتم العثور على منتجات</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Styles
const cardStyle = {
  background: "#fff",
  padding: "20px",
  borderRadius: "8px",
  marginBottom: "20px",
  boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
};

const sectionTitleStyle = {
  color: "#2563eb",
  fontWeight: "700",
  fontSize: "18px",
};

const labelStyle = {
  fontWeight: "600",
  color: "#334155",
  fontSize: "14px",
  marginBottom: "6px",
  display: "block",
};

const inputStyle = {
  width: "100%",
  padding: "10px 12px",
  borderRadius: "6px",
  border: "1px solid #cbd5e1",
  fontSize: "14px",
};

const errorStyle = {
  color: "#dc2626",
  fontSize: "13px",
  marginTop: "4px",
};

const itemCardStyle = {
  background: "#f8fafc",
  padding: "15px",
  borderRadius: "8px",
  marginBottom: "12px",
};

const submitBtnStyle = {
  background: "#2563eb",
  color: "#fff",
  padding: "12px 28px",
  borderRadius: "8px",
  border: "none",
  fontWeight: "700",
  fontSize: "16px",
  cursor: "pointer",
};

const compactItemStyle = {
  padding: "12px 8px",
  marginBottom: "6px",
  transition: "background 0.2s ease",
};

const compactInputStyle = {
  width: "100%",
  padding: "6px 10px",
  border: "1px solid #cbd5e1",
  borderRadius: "4px",
  fontSize: "13px",
  color: "#1e293b",
  outline: "none",
  transition: "all 0.2s",
};